/**
 * A utility icon button of the action bar's RIGHT side: Sound, Theme (R5),
 * Parental controls (fix Z2.4).
 */
import React, {useCallback, useState} from 'react';
import {Pressable, StyleSheet} from 'react-native';

import Icon, {IconName} from '../../../components/Icon';
import {RADIUS, SPACE} from '../../../theme/tokens';
import {useV2Theme} from '../../../theme/useV2Theme';
import {Txt} from '../../../ui';

const BUTTON = 36;
const ICON_SIZE = 17;

export type IconButtonProps = {
  icon: IconName;
  /** the a11y name — "Mute" / "Dark" and so on */
  label: string;
  onPress: () => void;
  testID?: string;
  /** fix Z2.4: a small bubble above the button while hovered, or none */
  tooltip?: string;
  /** fix Z2.4: stays on `tokens.well` even unhovered — "this one is open" */
  active?: boolean;
};

export default React.memo(function IconButtonView({
  icon,
  label,
  onPress,
  testID,
  tooltip,
  active,
}: IconButtonProps) {
  const tokens = useV2Theme(s => s.tokens);
  // fix Z2.4: the style-function `hovered` below still repaints the
  // background; the tooltip is a SIBLING element a style function cannot
  // conditionally render, so it needs its OWN hover signal in local state.
  const [hovered, setHovered] = useState(false);
  const onHoverIn = useCallback(() => setHovered(true), []);
  const onHoverOut = useCallback(() => setHovered(false), []);

  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      accessibilityLabel={label}
      style={({hovered: pressed}: any) => [
        styles.button,
        {backgroundColor: pressed || active ? tokens.well : 'transparent'},
      ]}>
      <Icon name={icon} size={ICON_SIZE} color={tokens.ink2} />
      {hovered && tooltip && (
        <Txt
          variant="caption"
          color={tokens.surface}
          style={
            [
              styles.tooltip,
              {
                backgroundColor: tokens.ink,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
              },
            ] as any
          }>
          {tooltip}
        </Txt>
      )}
    </Pressable>
  );
});

const styles = StyleSheet.create({
  button: {
    width: BUTTON,
    height: BUTTON,
    borderRadius: RADIUS.control,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tooltip: {
    position: 'absolute',
    bottom: BUTTON + SPACE.xs,
    right: 0,
    paddingHorizontal: SPACE.sm,
    paddingVertical: SPACE.xs / 2,
    borderRadius: RADIUS.control,
    zIndex: 30,
  },
});
